import React, { createContext, useState, useContext, ReactNode } from "react";
import { Button, Modal } from "react-bootstrap";

type AlertOptions = {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
};

type AlertContextProps = {
  showAlert: (options: AlertOptions) => void;
};

const AlertContext = createContext<AlertContextProps | undefined>(undefined);

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error("useAlert must be used within an AlertProvider");
  }
  return context;
};

export const AlertProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [alert, setAlert] = useState<AlertOptions | null>(null);

  const showAlert = (options: AlertOptions) => {
    setAlert(options);
  };

  const handleClose = () => setAlert(null);

  return (
    <AlertContext.Provider value={{ showAlert }}>
      {children}

      <Modal show={!!alert} onHide={handleClose} centered>
        {alert?.title && (
          <Modal.Header closeButton>
            <Modal.Title>{alert.title}</Modal.Title>
          </Modal.Header>
        )}
        <Modal.Body>{alert?.message}</Modal.Body>
        <Modal.Footer>
          {alert?.cancelText && (
            <Button variant="secondary" onClick={() => { 
              alert.onCancel?.();
              handleClose();
            }}>
              {alert.cancelText}
            </Button>
          )}
          <Button
            variant="primary"
            onClick={() => {
              alert?.onConfirm?.();
              handleClose();
            }}
          >
            {alert?.confirmText || "OK"}
          </Button>
        </Modal.Footer>
      </Modal>
    </AlertContext.Provider>
  );
};